const AUTH_STORAGE_KEY_PATTERNS = [
  /^sb-/i,
  /supabase\.auth/i,
  /gotrue/i,
  /lovable.*auth/i,
  /auth.*lovable/i,
  /^leadxkids:session_hint$/i,
];

const AUTH_COOKIE_KEY_PATTERNS = [
  /^sb-/i,
  /supabase/i,
  /gotrue/i,
  /lovable.*auth/i,
  /auth.*lovable/i,
];

const OAUTH_FLOW_STORAGE_KEY_PATTERNS = [
  /code[-_]?verifier/i,
  /pkce/i,
  /oauth.*state/i,
  /state.*oauth/i,
];

const AUTH_SESSION_STORAGE_KEY_PATTERNS = [
  /^sb-.*-auth-token$/i,
  /supabase\.auth/i,
  /gotrue/i,
  /lovable.*auth/i,
  /auth.*lovable/i,
  /^leadxkids:session_hint$/i,
];

const shouldClearAuthKey = (key: string) =>
  AUTH_STORAGE_KEY_PATTERNS.some((pattern) => pattern.test(key));

const isOAuthFlowStorageKey = (key: string) =>
  OAUTH_FLOW_STORAGE_KEY_PATTERNS.some((pattern) => pattern.test(key));

const shouldClearAuthSessionKey = (key: string) =>
  !isOAuthFlowStorageKey(key) &&
  AUTH_SESSION_STORAGE_KEY_PATTERNS.some((pattern) => pattern.test(key));

export const isFatalAuthResponseText = (text: string): boolean => {
  const value = text.toLowerCase();
  return (
    value.includes("bad_jwt") ||
    value.includes("invalid jwt") ||
    value.includes("token signature is invalid") ||
    value.includes("signature is invalid") ||
    value.includes("unable to parse or verify signature")
  );
};

export const isBackendAuthUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.hostname.endsWith(".supabase.co") ||
      parsed.pathname.startsWith("/auth/v1/") ||
      parsed.pathname.startsWith("/rest/v1/") ||
      parsed.pathname.startsWith("/functions/v1/")
    );
  } catch {
    return url.includes("supabase.co") || url.includes("/auth/v1/");
  }
};

const cookieClearDomains = (): string[] => {
  if (typeof window === "undefined") return [];
  const host = window.location.hostname;
  const parts = host.split(".").filter(Boolean);
  const domains = new Set<string>([host]);

  if (parts.length >= 2) {
    const root = parts.slice(-2).join(".");
    domains.add(root);
    domains.add(`.${root}`);
  }

  domains.add(`.${host}`);
  return Array.from(domains);
};

export function clearStaleAuthState(): void {
  if (typeof window === "undefined") return;

  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && shouldClearAuthKey(key)) window.localStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }

  try {
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const key = window.sessionStorage.key(i);
      if (key && shouldClearAuthKey(key)) window.sessionStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }

  try {
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0]?.trim();
      if (!name || !AUTH_COOKIE_KEY_PATTERNS.some((pattern) => pattern.test(name))) return;
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      for (const domain of cookieClearDomains()) {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; Secure; SameSite=None`;
      }
    });
  } catch {
    /* cookies unavailable */
  }
}

export function clearStoredAuthSessionsPreservingOAuthFlow(): void {
  if (typeof window === "undefined") return;

  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && shouldClearAuthSessionKey(key)) window.localStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }

  try {
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const key = window.sessionStorage.key(i);
      if (key && shouldClearAuthSessionKey(key)) window.sessionStorage.removeItem(key);
    }
  } catch {
    /* storage unavailable */
  }
}

export function clearAuthStateOnce(version: string): void {
  if (typeof window === "undefined") return;
  const key = "leadxkids:auth_cache_reset_version";
  try {
    if (window.localStorage.getItem(key) === version) return;
    clearStaleAuthState();
    window.localStorage.setItem(key, version);
  } catch {
    clearStaleAuthState();
  }
}

/**
 * Post-sign-in destination, persisted independently of the URL.
 *
 * The OAuth broker round trip can drop the `?redirect=` query on the return to
 * /auth, which used to silently dump paying users on /learning-journeys instead
 * of the trial checkout they clicked. Storing the intent survives that.
 */
const PENDING_REDIRECT_KEY = "leadxkids:pending_post_auth_redirect";
const PENDING_REDIRECT_TTL_MS = 30 * 60 * 1000;
/**
 * An OAuth hand-off is a much shorter-lived thing than a plain stored intent:
 * the round trip takes seconds, so 10 minutes is generous while keeping the
 * hijack window on an abandoned trial sign-in small.
 */
const PENDING_REDIRECT_OAUTH_TTL_MS = 10 * 60 * 1000;
/**
 * Legacy sessionStorage flag. Still written/read for backwards compatibility
 * with a tab mid-flight across a deploy, but the authoritative marker now
 * lives inside the localStorage record (and a cookie mirror) so it survives a
 * broker return in a different browsing context — an installed PWA handing off
 * to a Custom Tab loses sessionStorage entirely, which used to dump paying
 * users on /learning-journeys.
 */
const OAUTH_HANDOFF_KEY = "leadxkids:pending_post_auth_redirect_oauth";
const PENDING_REDIRECT_COOKIE = "lxk_par";

interface PendingRedirectRecord {
  path: string;
  at: number;
  oauth?: boolean;
}

const isSafeRedirectPath = (value: string) =>
  value.startsWith("/") && !value.startsWith("//");

const isFreshRecord = (record: PendingRedirectRecord) => {
  const ttl = record.oauth ? PENDING_REDIRECT_OAUTH_TTL_MS : PENDING_REDIRECT_TTL_MS;
  return Number.isFinite(record.at) && Date.now() - record.at <= ttl;
};

const parseRecord = (raw: string | null): PendingRedirectRecord | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingRedirectRecord>;
    if (!parsed?.path || !isSafeRedirectPath(parsed.path)) return null;
    if (!parsed.at) return null;
    return { path: parsed.path, at: parsed.at, oauth: parsed.oauth === true };
  } catch {
    return null;
  }
};

const writeCookieRecord = (record: PendingRedirectRecord): void => {
  try {
    const value = encodeURIComponent(JSON.stringify(record));
    const maxAge = Math.round(
      (record.oauth ? PENDING_REDIRECT_OAUTH_TTL_MS : PENDING_REDIRECT_TTL_MS) / 1000,
    );
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${PENDING_REDIRECT_COOKIE}=${value}; Max-Age=${maxAge}; path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies unavailable */
  }
};

const readCookieRecord = (): PendingRedirectRecord | null => {
  try {
    const entry = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${PENDING_REDIRECT_COOKIE}=`));
    if (!entry) return null;
    return parseRecord(decodeURIComponent(entry.slice(PENDING_REDIRECT_COOKIE.length + 1)));
  } catch {
    return null;
  }
};

const clearCookieRecord = (): void => {
  try {
    document.cookie = `${PENDING_REDIRECT_COOKIE}=; Max-Age=0; path=/; SameSite=Lax`;
  } catch {
    /* cookies unavailable */
  }
};

const readLocalRecord = (): PendingRedirectRecord | null => {
  try {
    return parseRecord(window.localStorage.getItem(PENDING_REDIRECT_KEY));
  } catch {
    return null;
  }
};

const writeLocalRecord = (record: PendingRedirectRecord): void => {
  try {
    window.localStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable */
  }
};

/** localStorage first, cookie as the fallback channel. */
const readRecord = (): PendingRedirectRecord | null => {
  if (typeof window === "undefined") return null;
  const local = readLocalRecord();
  const record = local ?? readCookieRecord();
  if (!record) return null;
  if (!isFreshRecord(record)) {
    clearPendingAuthRedirect();
    return null;
  }
  return record;
};

export function storePendingAuthRedirect(path: string): void {
  if (typeof window === "undefined" || !isSafeRedirectPath(path)) return;
  // An OAuth return can preserve the original ?redirect=. In that case Auth
  // mounts again and stores the same path a second time. Do not downgrade the
  // existing oauth:true record: doing so made the local copy shadow the cookie
  // mirror and caused the hand-off to be reported as absent on the return leg.
  const existing = readLocalRecord() ?? readCookieRecord();
  const preserveOAuth = existing?.path === path && existing.oauth === true && isFreshRecord(existing);
  const record: PendingRedirectRecord = preserveOAuth
    ? { path, at: existing.at, oauth: true }
    : { path, at: Date.now() };
  writeLocalRecord(record);
  if (record.oauth) writeCookieRecord(record);
}

export function readPendingAuthRedirect(): string | null {
  return readRecord()?.path ?? null;
}

/**
 * Marks the stored intent as belonging to an in-flight OAuth hand-off. Without
 * this distinction an abandoned trial sign-in would hijack a later, unrelated
 * sign-in for the rest of the TTL window.
 */
export function markPendingAuthRedirectOAuthHandoff(): void {
  if (typeof window === "undefined") return;
  const existing = readLocalRecord() ?? readCookieRecord();
  if (existing) {
    // Preserve the original timestamp so an already-stale intent stays stale.
    const record: PendingRedirectRecord = { ...existing, oauth: true };
    writeLocalRecord(record);
    writeCookieRecord(record);
  }
  try {
    window.sessionStorage.setItem(OAUTH_HANDOFF_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

export function hasPendingAuthRedirectOAuthHandoff(): boolean {
  if (typeof window === "undefined") return false;
  const record = readLocalRecord() ?? readCookieRecord();
  if (record?.oauth) return true;
  try {
    return window.sessionStorage.getItem(OAUTH_HANDOFF_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearPendingAuthRedirect(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PENDING_REDIRECT_KEY);
  } catch {
    /* storage unavailable */
  }
  try {
    window.sessionStorage.removeItem(OAUTH_HANDOFF_KEY);
  } catch {
    /* storage unavailable */
  }
  clearCookieRecord();
}

